import React, { useState, useEffect, useRef } from 'react';
import AspiForm from './AspiForm';
import AspiList from './AspiList';
import PairingForm, { PAIR_GROUPS } from './PairingForm';
import ChartGuide from './ChartGuide';
import ShareLinkButton from './ShareLinkButton';
import { buildSheetPath, parseDeepLink } from './deepLinks';
import PairingList from './PairingList';
import OnavForm from './OnavForm';
import OnavList from './OnavList';
import OnavChart from './OnavChart';
import OnavDetailSections from './OnavDetail';
import PdfButton from './PdfButton';
import ProfilePage from './ProfilePage';
import RadarChart from './RadarChart';
import WineCatalog from './WineCatalog';
import DishCatalog from './DishCatalog';
import BeerCatalog from './BeerCatalog';
import FoodSheetCatalog from './FoodSheetCatalog';
import ProductCatalog from './ProductCatalog';
import ProductSheetDetail from './ProductSheetDetail';
import CoffeeForm from './CoffeeForm';
import CoffeeList from './CoffeeList';
import OilForm from './OilForm';
import OilList from './OilList';
import TeaForm from './TeaForm';
import TeaList from './TeaList';
import VinegarForm from './VinegarForm';
import VinegarList from './VinegarList';
import { PRODUCT_CATEGORIES } from './productScoring';
import AttachmentList from './AttachmentList';
import { useI18n, LANGS } from './i18n';
import { exportPdf } from './pdfExport';
import { uploadAttachments, describeUploadFailure } from './attachments';
import { FOOD_PARAMS, WINE_PARAMS } from './codes';
import {
  initKeycloak, authFetch, getUser, logout, login as keycloakLogin, register as keycloakRegister,
  isAuthenticated, SSO_ENABLED,
} from './auth';
import './App.css';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

// Selector of the AIS graphic sheet (SVG of the Mercadini radar) that is embedded in the pairing PDF
const RADAR_SELECTOR = '.mercadini-svg';

// Detail row group: translated label (<ns>.p.<field>) + value/max
// showEmpty: Mercadini parameters can be "not filled" (null): they are listed with an em dash instead of being hidden
function DetailGrid({ ns, item, rows, showEmpty = false }) {
  const { t } = useI18n();
  const isEmpty = (value) => value === null || value === undefined;
  return (
    <div className="detail-grid">
      {rows.filter(([key]) => showEmpty || !isEmpty(item[key])).map(([key, max]) => (
        <span key={key}>{t(`${ns}.p.${key}`)}: {isEmpty(item[key]) ? '—' : `${item[key]}${max ? `/${max}` : ''}`}</span>
      ))}
    </div>
  );
}

const ASPI_VISUAL = [['clarity', 5], ['transparency', 5], ['color_intensity', 5], ['consistency', 5]];
const ASPI_OLFACTORY = [['olfactory_intensity', 5], ['olfactory_complexity', 5], ['olfactory_quality', 5]];
const ASPI_GUSTATORY = [['gustatory_sugars', 5], ['gustatory_alcohols', 5], ['gustatory_polyols', 5], ['gustatory_acids', 5],
  ['gustatory_tannins', 5], ['gustatory_minerals', 5], ['gustatory_balance', 5], ['gustatory_intensity', 5],
  ['gustatory_persistence', 5], ['gustatory_quality', 5]];
const PAIRING_FOOD = FOOD_PARAMS.map((k) => [k, 10]);
const PAIRING_WINE = WINE_PARAMS.map((k) => [k, 10]);

// Products (Catalogue > Products): one sheet form/list per category (views are named products-<category>-<form|list|detail|edit>)
const PRODUCT_FORMS = { coffee: CoffeeForm, tea: TeaForm, oil: OilForm, vinegar: VinegarForm };
const PRODUCT_LISTS = { coffee: CoffeeList, tea: TeaList, oil: OilList, vinegar: VinegarList };

// ── Deep links (/sheet/<type>/<uuid>): which view shows a linked sheet, and which link a view on screen has ──
const viewForLink = (type) => (
  type === 'onav' || type === 'aspi' || type === 'pairing' ? `${type}-detail`
    : type === 'food' ? 'catalog-food'
      : `products-${type}-detail`
);
// Link of what is on screen (a sheet detail or edit view, or one food sheet), or null: the URL is '/' in every other view
function linkForState(view, item, foodFocus) {
  if (view === 'catalog-food' && foodFocus) return { type: 'food', id: foodFocus.id };
  if (!item) return null;
  const sheet = /^(onav|aspi|pairing)-(detail|edit)$/.exec(view);
  if (sheet) return { type: sheet[1], id: item.id };
  const product = /^products-(coffee|tea|oil|vinegar)-(detail|edit)$/.exec(view);
  return product ? { type: product[1], id: item.id } : null;
}
// The list a detail/edit view comes from (used when the browser goes back to '/')
const homeViewFor = (view) => {
  const sheet = /^(onav|aspi|pairing)-(detail|edit)$/.exec(view);
  if (sheet) return `${sheet[1]}-list`;
  const product = /^(products-(?:coffee|tea|oil|vinegar))-(detail|edit)$/.exec(view);
  if (product) return `${product[1]}-list`;
  return view === 'link-error' || view === 'link-loading' ? 'onav-form' : view;
};

function App() {
  const { t, tc, lang, setLang, formatDate, formatNumber } = useI18n();
  const [aspiSheets, setAspiSheets] = useState([]);
  const [pairings, setPairings] = useState([]);
  const [onavSheets, setOnavSheets] = useState([]);
  const [productSheets, setProductSheets] = useState({ coffee: [], tea: [], oil: [], vinegar: [] });
  const [catalogCategory, setCatalogCategory] = useState('');     // category chosen in the Catalogue > Products entry ('' = all)
  const [view, setView] = useState('onav-form');
  const [selectedItem, setSelectedItem] = useState(null);
  const [message, setMessage] = useState(null);
  const [kcReady, setKcReady] = useState(false);
  const [me, setMe] = useState(null);
  const [foodFocus, setFoodFocus] = useState(null);       // one food sheet opened from a link (shown alone in Catalogue > Food sheets)
  const [linkError, setLinkError] = useState(null);       // { link, status } when the sheet of a link cannot be opened
  const [routerReady, setRouterReady] = useState(false);  // the URL is followed only after the first link has been handled
  const firstSync = useRef(true);
  // Opening a category's sheet flow keeps that category in the Catalogue > Products navigation
  useEffect(() => {
    const [, category] = view.startsWith('products-') ? view.split('-') : [];
    if (category) setCatalogCategory(category);
  }, [view]);

  const loadList = async (path, setter) => {
    try {
      const res = await authFetch(`${API_URL}/${path}`);
      if (res && res.ok) setter(await res.json());
    } catch { /* ignore */ }
  };
  const fetchAspiSheets = () => loadList('aspi', setAspiSheets);
  const fetchOnavSheets = () => loadList('onav', setOnavSheets);
  const fetchPairings = () => loadList('pairings', setPairings);
  const fetchProductSheets = (category) => loadList(category, (list) => setProductSheets((prev) => ({ ...prev, [category]: list })));

  // Opens the sheet named by a deep link (the same detail views the archives open). Not found / private / error -> 'link-error' view.
  const openDeepLink = async (link) => {
    setLinkError(null);
    setView('link-loading');
    try {
      const res = await authFetch(`${API_URL}${link.route}/${link.id}`);
      if (res && res.ok) {
        const item = await res.json();
        setFoodFocus(link.type === 'food' ? item : null);
        setSelectedItem(link.type === 'food' ? null : item);
        setView(viewForLink(link.type));
        return;
      }
      setLinkError({ link, status: res ? res.status : 0 });
    } catch {
      setLinkError({ link, status: 0 });
    }
    setSelectedItem(null);
    setFoodFocus(null);
    setView('link-error');
  };

  useEffect(() => {
    initKeycloak().then(async () => {
      setKcReady(true);
      // The sheet the visitor wanted before signing in (saved by login()/register()), then the link in the address bar
      let link = parseDeepLink(window.location.pathname);
      try {
        const returnTo = sessionStorage.getItem('tnh_return_to');
        if (returnTo) {
          sessionStorage.removeItem('tnh_return_to');
          const back = parseDeepLink(returnTo);
          if (back && !link && isAuthenticated()) {
            window.history.replaceState(null, '', buildSheetPath(back.type, back.id));
            link = back;
          }
        }
      } catch { /* ignore */ }
      const opening = link ? openDeepLink(link) : null;
      try {
        if (sessionStorage.getItem('tnh_account_deleted')) {
          sessionStorage.removeItem('tnh_account_deleted');
          if (!isAuthenticated()) showMsg('success', t('app.msg.account_deleted'));
          else showMsg('error', t('app.msg.account_sso_active'));
        }
      } catch { /* ignore */ }
      fetchAspiSheets();
      fetchPairings();
      fetchOnavSheets();
      PRODUCT_CATEGORIES.forEach(fetchProductSheets);
      if (isAuthenticated()) {
        try {
          const res = await authFetch(`${API_URL}/me`);
          if (res && res.ok) setMe(await res.json());
        } catch { /* ignore */ }
      }
      if (opening) await opening;
      setRouterReady(true);
    });
  }, []);

  // The address bar follows what is on screen: a sheet detail has its link, every other view is '/'. Opening a detail pushes an
  // entry (the browser back button leaves it); the first synchronisation only normalises the URL (replace).
  useEffect(() => {
    if (!routerReady || view === 'link-error' || view === 'link-loading') return;
    const link = linkForState(view, selectedItem, foodFocus);
    const target = (link && buildSheetPath(link.type, link.id)) || '/';
    if (window.location.pathname !== target) {
      if (firstSync.current) window.history.replaceState(null, '', target);
      else window.history.pushState(null, '', target);
    }
    firstSync.current = false;
  }, [routerReady, view, selectedItem, foodFocus]);

  // Browser back/forward: to a link -> open it; to any other path -> the list the detail came from
  useEffect(() => {
    const onPop = () => {
      const link = parseDeepLink(window.location.pathname);
      if (link) { openDeepLink(link); return; }
      setLinkError(null);
      setSelectedItem(null);
      setFoodFocus(null);
      setView((current) => homeViewFor(current));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // A food sheet opened from a link is shown alone only while the Food sheets view is on screen
  useEffect(() => {
    if (view !== 'catalog-food' && foodFocus) setFoodFocus(null);
  }, [view, foodFocus]);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  // The sheet owner (registered user) may edit, delete and attach files; anonymous sheets have no owner.
  const isOwner = (item) => !!me && !!item.owner_id && item.owner_id === me.id;

  // ── Create (POST), then upload the files chosen in the form ──
  const createSheet = async ({ path, data, attachments, refetch, savedKey, errorKey }) => {
    try {
      const res = await authFetch(`${API_URL}/${path}`, { method: 'POST', body: JSON.stringify(data) });
      if (!res || !res.ok) throw new Error();
      const created = await res.json();
      await refetch();
      if (attachments && attachments.length > 0) {
        const { uploaded, failed } = await uploadAttachments(created.id, attachments);
        if (failed.length > 0) showMsg('error', `${t(savedKey)} ${failed.map((f) => `${f.name}: ${describeUploadFailure(f, t)}`).join(' · ')}`);
        else showMsg('success', `${t(savedKey)} ${t('attachments.uploaded', { n: uploaded })}`);
      } else {
        showMsg('success', t(savedKey));
      }
      return created;
    } catch {
      showMsg('error', t(errorKey));
      return null;
    }
  };

  // ── Update (PUT) ──
  const updateSheet = async (path, id, data, refetch, detailView) => {
    try {
      const res = await authFetch(`${API_URL}/${path}/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      if (!res || !res.ok) throw new Error();
      const updated = await res.json();
      await refetch();
      setSelectedItem(updated);
      setView(detailView);
      showMsg('success', t('app.msg.saved_changes'));
    } catch {
      showMsg('error', t('app.msg.error_changes'));
    }
  };

  // ── Delete ──
  const deleteSheet = async (path, id, confirmKey, refetch, listView) => {
    if (!window.confirm(t(confirmKey))) return;
    try {
      const res = await authFetch(`${API_URL}/${path}/${id}`, { method: 'DELETE' });
      if (!res || !res.ok) throw new Error();
      await refetch();
      setSelectedItem(null);
      setView(listView);
    } catch { alert(t('app.msg.error_delete')); }
  };

  const siteName = t('app.site_name');
  const siteSubtitle = t('app.subtitle');
  const exportSheet = (path, chartSelector) => exportPdf({
    path, data: selectedItem, chartSelector, errorMessage: t('ui.export_error'),
  });

  // Active tab
  const activeTab =
    view === 'profile' ? 'profile' :
    view === 'link-error' || view === 'link-loading' ? 'none' :
    view.startsWith('catalog') || view.startsWith('products') ? 'catalog' :
    view.startsWith('onav') ? 'onav' :
    view.startsWith('pairing') ? 'pairing' : 'aspi';

  const kcUser = kcReady ? getUser() : null;

  if (!kcReady) {
    return (
      <div className="app" style={{ textAlign: 'center', paddingTop: 100 }}>
        <h2 style={{ color: '#722f37' }}>{t('app.loading')}</h2>
        <p>{t('app.connecting')}</p>
      </div>
    );
  }

  const sheet = selectedItem;
  const owner = !!sheet && isOwner(sheet);
  const [, productCategory, productMode] = view.startsWith('products-') ? view.split('-') : [];
  const ProductForm = PRODUCT_FORMS[productCategory];
  const ProductList = PRODUCT_LISTS[productCategory];
  const productRefetch = () => fetchProductSheets(productCategory);
  const currentProductCategory = productCategory || catalogCategory;   // category selected in the products navigation ('' = all)
  const inProducts = view === 'catalog-products' || !!productCategory;
  // Category chip of the products navigation: inside the sheet flows it keeps the mode (form or archive)
  const chooseProductCategory = (c) => {
    setCatalogCategory(c);
    setSelectedItem(null);
    if (!c) setView('catalog-products');
    else if (productCategory) setView(`products-${c}-${productMode === 'form' ? 'form' : 'list'}`);
  };

  return (
    <div className="app">
      {/* ── Top bar: language + user ── */}
      <div className="user-bar">
        <select className="lang-select" value={lang} onChange={(e) => setLang(e.target.value)}
          aria-label={t('ui.language')} title={t('ui.language')}>
          {LANGS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
        {SSO_ENABLED && (kcUser ? (
          <>
            <span className="user-name" onClick={() => setView('profile')} style={{ cursor: 'pointer' }}>
              {kcUser.name || kcUser.email}
            </span>
            <button className="user-btn" onClick={() => setView('profile')}>{t('app.profile')}</button>
            <button className="user-btn logout" onClick={logout}>{t('app.logout')}</button>
          </>
        ) : (
          <>
            <button className="user-btn" onClick={() => keycloakLogin()}>{t('app.login')}</button>
            <button className="user-btn" onClick={() => keycloakRegister()}>{t('app.register')}</button>
          </>
        ))}
      </div>
      <header className="header">
        <h1>{siteName}</h1>
        <p className="subtitle">{siteSubtitle}</p>

        {/* ── Main tabs ── */}
        <div className="tabs">
          <button className={`tab ${activeTab === 'onav' ? 'active' : ''}`}
            onClick={() => { setView('onav-form'); setSelectedItem(null); }}>
            {t('app.tab.onav')}
          </button>
          <button className={`tab ${activeTab === 'pairing' ? 'active' : ''}`}
            onClick={() => { setView('pairing-form'); setSelectedItem(null); }}>
            {t('app.tab.pairing')}
          </button>
          <button className={`tab ${activeTab === 'aspi' ? 'active' : ''}`}
            onClick={() => { setView('aspi-form'); setSelectedItem(null); }}>
            {t('app.tab.aspi')}
          </button>
          <button className={`tab ${activeTab === 'catalog' ? 'active' : ''}`}
            onClick={() => { setView('catalog-wines'); setSelectedItem(null); }}>
            {t('app.tab.catalog')}
          </button>
        </div>

        {/* ── Sub-navigation of the active tab ── */}
        <nav className="nav sub-nav">
          {activeTab === 'onav' && <>
            <button className={view === 'onav-form' ? 'active' : ''}
              onClick={() => { setView('onav-form'); setSelectedItem(null); }}>
              {t('app.nav.new_sheet')}
            </button>
            <button className={view === 'onav-list' ? 'active' : ''}
              onClick={() => setView('onav-list')}>
              {t('app.nav.archive', { n: onavSheets.length })}
            </button>
          </>}
          {activeTab === 'pairing' && <>
            <button className={view === 'pairing-form' ? 'active' : ''}
              onClick={() => { setView('pairing-form'); setSelectedItem(null); }}>
              {t('app.nav.new_pairing')}
            </button>
            <button className={view === 'pairing-list' ? 'active' : ''}
              onClick={() => setView('pairing-list')}>
              {t('app.nav.archive', { n: pairings.length })}
            </button>
          </>}
          {activeTab === 'aspi' && <>
            <button className={view === 'aspi-form' ? 'active' : ''}
              onClick={() => { setView('aspi-form'); setSelectedItem(null); }}>
              {t('app.nav.new_sheet')}
            </button>
            <button className={view === 'aspi-list' ? 'active' : ''}
              onClick={() => setView('aspi-list')}>
              {t('app.nav.archive', { n: aspiSheets.length })}
            </button>
          </>}
          {activeTab === 'catalog' && <>
            <button className={view === 'catalog-wines' ? 'active' : ''}
              onClick={() => setView('catalog-wines')}>
              {t('app.nav.wines')}
            </button>
            <button className={view === 'catalog-dishes' ? 'active' : ''}
              onClick={() => setView('catalog-dishes')}>
              {t('app.nav.dishes')}
            </button>
            <button className={view === 'catalog-beers' ? 'active' : ''}
              onClick={() => setView('catalog-beers')}>
              {t('app.nav.beers')}
            </button>
            <button className={inProducts ? 'active' : ''}
              onClick={() => { setView('catalog-products'); setSelectedItem(null); }}>
              {t('app.nav.products')}
            </button>
            <button className={view === 'catalog-food' ? 'active' : ''}
              onClick={() => setView('catalog-food')}>
              {t('app.nav.food_sheets')}
            </button>
          </>}
        </nav>
      </header>

      {message && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      <main>
        {/* ── PROFILE ── */}
        {view === 'profile' && <ProfilePage />}

        {/* ── CATALOG ── */}
        {view === 'catalog-wines' && <WineCatalog />}
        {view === 'catalog-dishes' && <DishCatalog />}
        {view === 'catalog-beers' && <BeerCatalog />}
        {/* ── CATALOG > PRODUCTS: coffee, tea, oil, vinegar (catalogue of products + sheets of each category) ── */}
        {inProducts && (
          <>
            <div className="product-picker">
              {[''].concat(PRODUCT_CATEGORIES).map((c) => (
                <button type="button" key={c || 'all'} className={`chip ${currentProductCategory === c ? 'selected' : ''}`}
                  onClick={() => chooseProductCategory(c)}>
                  {c ? t(`products.category.${c}`) : t('products.category.all')}
                </button>
              ))}
            </div>
            {currentProductCategory && (
              <p className="product-actions">
                <button type="button" className={view === 'catalog-products' ? 'submit-btn' : 'reset-btn'}
                  onClick={() => { setView('catalog-products'); setSelectedItem(null); }}>
                  {t('app.nav.products')}
                </button>{' '}
                <button type="button" className={productMode === 'form' ? 'submit-btn' : 'reset-btn'}
                  onClick={() => { setView(`products-${currentProductCategory}-form`); setSelectedItem(null); }}>
                  {t('app.nav.new_sheet')}
                </button>{' '}
                <button type="button" className={productMode === 'list' || productMode === 'detail' ? 'submit-btn' : 'reset-btn'}
                  onClick={() => { setView(`products-${currentProductCategory}-list`); setSelectedItem(null); }}>
                  {t('app.nav.archive', { n: productSheets[currentProductCategory].length })}
                </button>
              </p>
            )}
            {view === 'catalog-products' && <ProductCatalog category={catalogCategory} />}
            {productMode === 'form' && (
              <ProductForm onSubmit={(data, attachments) => createSheet({
                path: productCategory, data, attachments, refetch: productRefetch,
                savedKey: 'products.msg.saved', errorKey: 'products.msg.error_save',
              })} />
            )}
            {productMode === 'edit' && sheet && (
              <ProductForm initialData={sheet}
                onSubmit={(data) => updateSheet(productCategory, sheet.id, data, productRefetch, `products-${productCategory}-detail`)}
                onCancel={() => setView(`products-${productCategory}-detail`)} />
            )}
            {productMode === 'list' && (
              <ProductList sheets={productSheets[productCategory]}
                onSelect={(s) => { setSelectedItem(s); setView(`products-${productCategory}-detail`); }} />
            )}
            {productMode === 'detail' && sheet && (
              <ProductSheetDetail category={productCategory} sheet={sheet} owner={owner}
                onBack={() => setView(`products-${productCategory}-list`)}
                onEdit={() => setView(`products-${productCategory}-edit`)}
                onDelete={() => deleteSheet(productCategory, sheet.id, 'products.msg.confirm_delete', productRefetch, `products-${productCategory}-list`)} />
            )}
          </>
        )}
        {view === 'catalog-food' && (
          <FoodSheetCatalog focusSheet={foodFocus} onShowAll={() => { setFoodFocus(null); }} />
        )}

        {/* ── Deep link: loading and "not found / private" ── */}
        {view === 'link-loading' && <div className="link-page"><p>{t('app.link.loading')}</p></div>}
        {view === 'link-error' && linkError && (
          <div className="link-page">
            <h2>{t(linkError.status === 404 ? 'app.link.not_found_title' : 'app.link.error_title')}</h2>
            <p>{t(linkError.status === 404 ? 'app.link.not_found_text' : 'app.link.error_text')}</p>
            <p className="form-buttons">
              {SSO_ENABLED && !isAuthenticated() && (
                <button type="button" className="submit-btn" onClick={() => keycloakLogin()}>{t('app.link.sign_in')}</button>
              )}
              <button type="button" className="reset-btn" onClick={() => { setLinkError(null); setView('onav-form'); }}>{t('app.link.go_home')}</button>
            </p>
          </div>
        )}

        {/* ── ONAV SHEET ── */}
        {view === 'onav-form' && (
          <OnavForm onSubmit={(data, attachments) => createSheet({
            path: 'onav', data, attachments, refetch: fetchOnavSheets,
            savedKey: 'app.msg.saved_onav', errorKey: 'app.msg.error_onav',
          })} />
        )}
        {view === 'onav-edit' && sheet && (
          <OnavForm initialData={sheet}
            onSubmit={(data) => updateSheet('onav', sheet.id, data, fetchOnavSheets, 'onav-detail')}
            onCancel={() => setView('onav-detail')} />
        )}
        {view === 'onav-list' && (
          <OnavList sheets={onavSheets}
            onSelect={(s) => { setSelectedItem(s); setView('onav-detail'); }} />
        )}
        {view === 'onav-detail' && sheet && (
          <div className="detail">
            <button className="back-btn" onClick={() => setView('onav-list')}>{t('ui.back_archive')}</button>
            <ShareLinkButton type="onav" id={sheet.id} visibility={sheet.visibility} isOwner={owner}
              onVisibilityChange={(v) => setSelectedItem({ ...sheet, visibility: v })} />
            <h2>{sheet.wine_name} {sheet.vintage || ''}</h2>
            <p><strong>{t('app.d.type')}:</strong> {tc('wine_type', sheet.wine_type) || '—'} {tc('classification', sheet.classification)}</p>
            <p><strong>{t('app.d.producer')}:</strong> {sheet.producer || '—'}</p>
            <p><strong>{t('app.d.alcohol')}:</strong> {sheet.abv ? formatNumber(sheet.abv) : '—'}% | <strong>{t('app.d.temp')}:</strong> {sheet.serving_temperature ? formatNumber(sheet.serving_temperature) : '—'}°C</p>
            <p><strong>{t('app.d.taster')}:</strong> {sheet.taster || '—'} | <strong>{t('app.d.date')}:</strong> {formatDate(sheet.tasting_date) || '—'}</p>
            {sheet.venue && <p><strong>{t('app.d.venue')}:</strong> {sheet.venue}</p>}

            <OnavDetailSections sheet={sheet} />

            <div className="onav-final-total" style={{ marginTop: 16 }}>
              <span className="onav-score">{sheet.total}/100</span>
              {sheet.verdict && (
                <span className={`pairing-badge ${sheet.verdict}`}>{tc('verdict', sheet.verdict)}</span>
              )}
            </div>

            {sheet.notes && <p style={{ marginTop: 12 }}><strong>{t('app.d.notes')}:</strong> {sheet.notes}</p>}

            <h3>{t('app.d.sec_chart')}</h3>
            <OnavChart data={sheet} />

            <AttachmentList sheetId={sheet.id} canEdit={owner} />

            <button className="submit-btn" style={{ marginRight: 8 }} onClick={() => exportSheet('onav')}>
              {t('ui.export_pdf')}
            </button>
            <PdfButton path={`onav/${sheet.id}/pdf?template=official`} label={t('onav.official_pdf')} className="submit-btn"
              hint={t('onav.official_pdf_hint')} fallbackName="onav-official-form.pdf" />
            {owner && (
              <>
                <button className="submit-btn" style={{ marginRight: 8 }} onClick={() => setView('onav-edit')}>
                  {t('ui.edit')}
                </button>
                <button className="delete-btn"
                  onClick={() => deleteSheet('onav', sheet.id, 'app.msg.confirm_delete_onav', fetchOnavSheets, 'onav-list')}>
                  {t('app.d.delete_sheet')}
                </button>
              </>
            )}
          </div>
        )}

        {/* ── PAIRING ── */}
        {view === 'pairing-form' && (
          <PairingForm onSubmit={(data, attachments) => createSheet({
            path: 'pairings', data, attachments, refetch: fetchPairings,
            savedKey: 'app.msg.saved_pairing', errorKey: 'app.msg.error_pairing',
          })} />
        )}
        {view === 'pairing-edit' && sheet && (
          <PairingForm initialData={sheet}
            onSubmit={(data) => updateSheet('pairings', sheet.id, data, fetchPairings, 'pairing-detail')}
            onCancel={() => setView('pairing-detail')} />
        )}
        {view === 'pairing-list' && (
          <PairingList
            pairings={pairings}
            onSelect={(p) => { setSelectedItem(p); setView('pairing-detail'); }}
          />
        )}
        {view === 'pairing-detail' && sheet && (
          <div className="detail">
            <button className="back-btn" onClick={() => setView('pairing-list')}>
              {t('ui.back_archive')}
            </button>
            <ShareLinkButton type="pairing" id={sheet.id} visibility={sheet.visibility} isOwner={owner}
              onVisibilityChange={(v) => setSelectedItem({ ...sheet, visibility: v })} />
            <h2>{sheet.dish_name}</h2>
            <p><strong>{t('app.d.wine')}:</strong> {sheet.wine_name} {sheet.vintage || ''}</p>
            <p><strong>{t('app.d.taster')}:</strong> {sheet.taster || '—'}</p>
            <p><strong>{t('app.d.date')}:</strong> {formatDate(sheet.tasting_date) || '—'}</p>
            {sheet.venue && <p><strong>{t('app.d.venue')}:</strong> {sheet.venue}</p>}
            {sheet.chef && <p><strong>{t('app.d.chef')}:</strong> {sheet.chef}</p>}

            <h3>{t('app.d.pairing_chart')}</h3>
            <RadarChart data={sheet} />
            <ChartGuide groups={PAIR_GROUPS} />

            <h3>{t('app.d.pairing_food')}</h3>
            <DetailGrid ns="pairing" item={sheet} rows={PAIRING_FOOD} showEmpty />

            <h3>{t('app.d.pairing_wine')}</h3>
            <DetailGrid ns="pairing" item={sheet} rows={PAIRING_WINE} showEmpty />

            <h3>{t('app.d.pairing_eval')}</h3>
            {sheet.pairing_outcome && (
              <p className={`pairing-badge-sm ${String(sheet.pairing_outcome).replace(/_/g, '-')}`}>
                {tc('outcome', sheet.pairing_outcome)}
              </p>
            )}
            {sheet.score != null && (
              <p className="score"><strong>{t('app.d.score')}:</strong> {formatNumber(sheet.score)}/10</p>
            )}
            {sheet.notes && <p><strong>{t('app.d.notes')}:</strong> {sheet.notes}</p>}

            <AttachmentList sheetId={sheet.id} canEdit={owner} />

            <button className="submit-btn" style={{ marginRight: 8 }} onClick={() => exportSheet('pairings', RADAR_SELECTOR)}>
              {t('ui.export_pdf')}
            </button>
            {owner && (
              <>
                <button className="submit-btn" style={{ marginRight: 8 }} onClick={() => setView('pairing-edit')}>
                  {t('ui.edit')}
                </button>
                <button className="delete-btn"
                  onClick={() => deleteSheet('pairings', sheet.id, 'app.msg.confirm_delete_pairing', fetchPairings, 'pairing-list')}>
                  {t('app.d.delete_pairing')}
                </button>
              </>
            )}
          </div>
        )}

        {/* ── ASPI SHEET ── */}
        {view === 'aspi-form' && (
          <AspiForm onSubmit={(data, attachments) => createSheet({
            path: 'aspi', data, attachments, refetch: fetchAspiSheets,
            savedKey: 'app.msg.saved_sheet', errorKey: 'app.msg.error_sheet',
          })} />
        )}
        {view === 'aspi-edit' && sheet && (
          <AspiForm initialData={sheet}
            onSubmit={(data) => updateSheet('aspi', sheet.id, data, fetchAspiSheets, 'aspi-detail')}
            onCancel={() => setView('aspi-detail')} />
        )}
        {view === 'aspi-list' && (
          <AspiList
            sheets={aspiSheets}
            onSelect={(s) => { setSelectedItem(s); setView('aspi-detail'); }}
          />
        )}
        {view === 'aspi-detail' && sheet && (
          <div className="detail">
            <button className="back-btn" onClick={() => setView('aspi-list')}>{t('ui.back_archive')}</button>
            <ShareLinkButton type="aspi" id={sheet.id} visibility={sheet.visibility} isOwner={owner}
              onVisibilityChange={(v) => setSelectedItem({ ...sheet, visibility: v })} />
            <h2>{sheet.wine_name} {sheet.vintage || ''}</h2>
            <p><strong>{t('app.d.producer')}:</strong> {sheet.producer || '—'}</p>
            <p><strong>{t('app.d.zone')}:</strong> {sheet.production_area || '—'}</p>
            {sheet.classification && <p><strong>{t('aspi.p.classification')}:</strong> {tc('classification', sheet.classification)}</p>}
            {sheet.wine_type && <p><strong>{t('aspi.p.wine_type')}:</strong> {tc('wine_type', sheet.wine_type)}</p>}
            {sheet.tasting_temperature !== null && sheet.tasting_temperature !== undefined && (
              <p><strong>{t('aspi.p.tasting_temperature')}:</strong> {formatNumber(sheet.tasting_temperature)} °C</p>
            )}
            <p><strong>{t('app.d.taster')}:</strong> {sheet.taster || '—'}</p>
            <p><strong>{t('app.d.date')}:</strong> {formatDate(sheet.tasting_date) || '—'}</p>
            {sheet.venue && <p><strong>{t('app.d.venue')}:</strong> {sheet.venue}</p>}

            <h3>{t('app.d.aspi_visual')}</h3>
            <DetailGrid ns="aspi" item={sheet} rows={ASPI_VISUAL} />
            <p><strong>{t('aspi.p.color_hue')}:</strong> {tc('hue', sheet.color_hue) || '—'}</p>
            {sheet.visual_reflections && <p><strong>{t('aspi.p.visual_reflections')}:</strong> {sheet.visual_reflections}</p>}
            {sheet.legs && <p><strong>{t('aspi.p.legs')}:</strong> {sheet.legs}</p>}

            <h3>{t('app.d.aspi_olfactory')}</h3>
            <DetailGrid ns="aspi" item={sheet} rows={ASPI_OLFACTORY} />
            {sheet.olfactory_description && (
              <p><strong>{t('app.d.description')}:</strong> {sheet.olfactory_description}</p>
            )}
            {sheet.olfactory_recognition && (
              <p><strong>{t('aspi.p.olfactory_recognition')}:</strong> {sheet.olfactory_recognition}</p>
            )}

            <h3>{t('app.d.aspi_gustatory')}</h3>
            <DetailGrid ns="aspi" item={sheet} rows={ASPI_GUSTATORY} />

            <h3>{t('app.d.aspi_final')}</h3>
            <p><strong>{t('aspi.p.evolution_state')}:</strong> {tc('evolution_state', sheet.evolution_state)}</p>
            <p><strong>{t('aspi.p.harmony')}:</strong> {sheet.harmony}/5</p>
            {['aging_potential', 'grape_identification', 'pairing_suggestion', 'glass_shape'].map((k) => (
              sheet[k] ? <p key={k}><strong>{t(`aspi.p.${k}`)}:</strong> {sheet[k]}</p> : null
            ))}
            {sheet.serving_temperature !== null && sheet.serving_temperature !== undefined && (
              <p><strong>{t('aspi.p.serving_temperature')}:</strong> {formatNumber(sheet.serving_temperature)} °C</p>
            )}
            {sheet.value_for_money !== null && sheet.value_for_money !== undefined && (
              <p><strong>{t('aspi.p.value_for_money')}:</strong> {sheet.value_for_money}/5</p>
            )}
            {sheet.final_score && (
              <p className="score"><strong>{t('aspi.p.final_score')}:</strong> {formatNumber(sheet.final_score)}</p>
            )}
            {sheet.notes && <p><strong>{t('app.d.notes')}:</strong> {sheet.notes}</p>}

            <AttachmentList sheetId={sheet.id} canEdit={owner} />

            <button className="submit-btn" style={{ marginRight: 8 }} onClick={() => exportSheet('aspi')}>
              {t('ui.export_pdf')}
            </button>
            {owner && (
              <>
                <button className="submit-btn" style={{ marginRight: 8 }} onClick={() => setView('aspi-edit')}>
                  {t('ui.edit')}
                </button>
                <button className="delete-btn"
                  onClick={() => deleteSheet('aspi', sheet.id, 'app.msg.confirm_delete_sheet', fetchAspiSheets, 'aspi-list')}>
                  {t('app.d.delete_sheet')}
                </button>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
